import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../../utils/api";
import { Validation } from "../../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import { Member } from "../../../../../@types/member.types";
import JSONToExcel from "../../../../../utils/commonFunctions/JSONToExcel";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { compareInvalidDates } from "../../../../../utils/commonFunctions/date";
import { Plan, PlanPopOverContent, PlanStatus } from "../../../../../@types/plan.types";
import { PLAN_NAME, PLAN_STATUS } from "../../../../../constants/plan";
import { useEmployerRoute } from "../../../../../hooks";
import { Group } from "../../../../../@types/group.types";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { Enrollment } from "../../../../../@types/enrollment.types";
import { PLAN_NAMES } from "../../../../../constants/planNames";

const chipMessage = "This report will pull all the Active Group Plan(s) with effective date as null.";

const ActiveGroupPlanNullEffectiveDateReport = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		alternative_report_name: "",
		group_number: null,
		plan_name: "",
		is_migrated: null
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [plans, setPlans] = useState([]);
	const [selectedPlan, setSelectedPlan] = useState<Plan>();
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, plan_name } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		// total deduction validation checking
		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (plan_name.length === 0) {
			_validation.current.reportFieldDetails["plan_name"] = "Plan is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			if (name === "is_migrated") {
				setReportFieldDetails(
					Object.assign({}, reportFieldDetails, {
						[name]: value === "Yes" ? true : value === "No" ? false : null
					})
				);
			} else {
				setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
			}
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Active Group Plan Effective Date Null Report Generation");
					const { group_number, plan_name, is_migrated } = reportFieldDetails;

					const activeGroupPlanNullEffectiveDateReportPayload = {
						group_number,
						plan_name: plan_name === "All" ? plan_name.toLowerCase() : plan_name,
						is_migrated: is_migrated ? is_migrated : undefined
					};

					// console.log(
					// 	"activeGroupPlanNullEffectiveDateReportPayload: ",
					// 	activeGroupPlanNullEffectiveDateReportPayload
					// );

					// API Calling needs to be updated.
					const response: Enrollment[] = await trackPromise(
						api.groupReport.activeGroupPlanNullEffectiveDateReport(
							activeGroupPlanNullEffectiveDateReportPayload
						)
					);

					if (response) {
						// console.log("Active Group Plan Effective Date Null Report", response);

						const fileName = reportFieldDetails.alternative_report_name
							? reportFieldDetails.alternative_report_name
							: "Active Group Plan Effective Date Null Report";

						const excelPayload = {
							user: {
								role: user?.role,
								name: user?.first_name + " " + user?.last_name
							},
							reportFieldDetails,
							response
						};

						await trackPromise(JSONToExcel(excelPayload, fileName));

						setTimeout(() => {
							setSnackbarAPICallProps(
								Object.assign({}, snackbarAPICallProps, {
									open: true,
									message:
										"Active Group Plan Effective Date Null Report Fetched Successfully and File Downloaded!",
									severity: "success"
								})
							);
						}, 2000);
					}
				}
			} catch (error) {
				alert("Active Group Plan Effective Date Null Report Generation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.active_group_plan_effective_date_null_report);
		getAssignGroups();
	}, [getAssignGroups, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		if (reportFieldDetails.date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { date: "" }));
	}, [reportFieldDetails]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Active Group Plan Effective Date Null Report Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Plan",
							name: "plan_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.plan_name,
							type: "select",
							options: [
								"All",
								PLAN_NAMES.KSOP,
								PLAN_NAMES.health_plan,
								PLAN_NAMES.clinic_card,
								PLAN_NAMES.helping_our_own,
								PLAN_NAMES.group_life_insurance,
								PLAN_NAMES.christmas_club_election_form,
								PLAN_NAMES.group_short_term_disability,
								PLAN_NAMES.group_long_term_disability_class1,
								PLAN_NAMES.group_long_term_disability_class2
							]
						},
						{
							label: "Migrated",
							name: "is_migrated",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								reportFieldDetails.is_migrated === true
									? "Yes"
									: reportFieldDetails.is_migrated === false
									? "No"
									: null,
							type: "select",
							options: ["Yes", "No"]
						},
						{
							label: "Alternative Report Name",
							name: "alternative_report_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Enter alternative report name",
							value: reportFieldDetails.alternative_report_name,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [
		assignedGroups,
		reportFieldDetails,
		handleChange,
		plans,
		isEmployerRoute,
		queryGroupNumber,
		selectedGroup?.name
	]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={() => null}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default ActiveGroupPlanNullEffectiveDateReport;
